import { Inject, Injectable } from '@angular/core';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { Salesperson } from '@galaxy/types';
import { filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { SalespersonOption } from '../filter-options/salesperson-option.model';

import {
  ALL_SALESPEOPLE_TOKEN,
  INIT_WITH_CUR_SALESPERSON_TOKEN,
  SALESPERSON_ID_TOKEN,
  SALESPERSON_NAME_TOKEN,
} from './tokens';

interface SalespersonMultiSelectStore {
  selectableSalespeople$: Observable<SalespersonOption[]>;
  selectedSalespeople$: Observable<SalespersonOption[]>;

  setSelectedSalespeople(salespeopleOptions: string[]): void;
}

/**
 * @description
 * Intent for the MultiSelectStoreServices is to combine their emissions wrapped by your FilterService.
 * Then a feature service can use those to make api calls (for backend filtering) by combining the changes in filters,
 * search term, page cursor, etc..., OR could do frontend filtering.
 *
 * @usageNotes
 * ```ts
 * // Best practice to have default values to load the content before all the filter values have loaded (may not be possible in all cases).
 * // This approach allows a filter's selectable values to be loaded lazily, and allows a user to interact with filters that have loaded in.
 * const defaultFilterValues: T = ...
 * @Injectable()
 * class MyFilterService<T> {
 *   readonly selectedFilters$: Observable<T>;
 *   constructor(
 *     private readonly spFilter: SalespersonMultiSelectStoreService,
 *     private readonly spFilter: SalesTeamMultiSelectStoreService,
 *   ) {
 *     this.selectedFilters$ = combineLatest([this.spFilter.selectedSalespeople$, this.stFilter.selectedSalesTeams$]).pipe(
 *       map(([peopleFilters, teamFilters]) => {
 *         return <T>{people: peopleFilters, teams: teamFilters};
 *       }),
 *       startsWith(defaultFilterValues),
 *     );
 *   }
 * }
 * ```
 */

/**
 * Manages the state of salesperson multi select.
 * Provides app state if provided in root (all instances share the same state and will effect each other),
 * Provides state for group of components if injected in a module (all instances in components of that module share state),
 * or directly on a component for component state (ex: page, table)
 */
@Injectable()
export class SalespersonMultiSelectStoreService implements SalespersonMultiSelectStore {
  readonly selectableSalespeople$: Observable<SalespersonOption[]>;
  private readonly selectedSalespeople$$ = new ReplaySubject<SalespersonOption[]>(1);
  readonly selectedSalespeople$: Observable<SalespersonOption[]> = this.selectedSalespeople$$.asObservable();
  private readonly allowSetSalespeople$: Observable<boolean>;

  constructor(
    @Inject(ALL_SALESPEOPLE_TOKEN) private readonly salespeople$: Observable<Salesperson[]>,
    @Inject(SALESPERSON_ID_TOKEN) private readonly salespersonId$: Observable<string>,
    @Inject(SALESPERSON_NAME_TOKEN) private readonly salespersonName$: Observable<string>,
    @Inject(INIT_WITH_CUR_SALESPERSON_TOKEN) private readonly initWithLoggedInSalesperson: boolean,
  ) {
    this.selectableSalespeople$ = this.salespeople$.pipe(
      map((sp) => sp.map((s) => new SalespersonOption({ id: s.id, name: s.fullName }))),
    );

    if (this.initWithLoggedInSalesperson) {
      const initToLoggedInUser$ = combineLatest([this.salespersonId$, this.salespersonName$]).pipe(
        take(1),
        filter(([id]) => !!id),
        map(([id, name]) => new SalespersonOption({ id: id, name: name })),
        tap((sp) => this.selectedSalespeople$$.next([sp])),
      );
      initToLoggedInUser$.subscribe();
    }

    this.allowSetSalespeople$ = this.salespeople$.pipe(
      map((salespeople) => salespeople.length > 0),
      shareReplay(1),
    );
  }

  getSalesPeopeOptionsFromIds$(ids: string[]): Observable<string[]> {
    return this.selectableSalespeople$.pipe(
      map((sps) => sps.filter((sp) => ids.includes(sp.id))),
      map((sps) => sps.map((sp) => `${sp.id}:${sp.fullName}`)),
    );
  }

  /**
   * @param salespeopleOptions - array of `${salespersonId}:${salespersonName}` strings
   */
  setSelectedSalespeople(salespeopleOptions: string[]): void {
    this.allowSetSalespeople$
      .pipe(
        take(1),
        filter((allow) => allow),
      )
      .subscribe(() => {
        const spOptions = [...new Set(salespeopleOptions)] as string[];
        const salespeople = (spOptions || []).map((o) => SalespersonOption.fromOptionValue(o));
        this.selectedSalespeople$$.next(salespeople);
      });
  }
}
